import { ArrayDataSource } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
// Models
import {
  User,
  AutopilotTrajectory,
  AutopilotCommand,
  AutopilotCommandEnum,
  Directory,
  PlanningItem,
  LinkedList,
  TrajectoryState,
} from '@mocca-technologies/model';
// Components
import { DirectoryManagerComponent } from '../../directory-manager/directory-manager.component';
// Services
import { AutopilotService } from 'src/app/services/ap-operations.service';
import { DirectoryService } from 'src/app/services/directory-manager.service';

@Component({
  selector: 'app-left-panel-tree',
  templateUrl: './left-panel-tree.component.html',
  styleUrls: ['./left-panel-tree.component.scss'],
})

export class LeftPanelTreeComponent implements OnInit {
  user = JSON.parse(localStorage.getItem('user')) as User;
  profile: string = 'auth';

  tree: LinkedList<Directory>;
  dataSource: ArrayDataSource<Directory>;
  treeControl = new FlatTreeControl<Directory>(
    (node) => node.level,
    (node) => true,
  );

  expandNode = (node: any) => {
    node.isExpanded = !node.isExpanded;
    this.ref.detectChanges();
  };

  selectTrackOnMap = (node: any) => {
    if (
      node.name !== 'Departure' &&
      node.name !== 'Mission' &&
      node.name !== 'Arrival'
    ) {
      this.selectTrack.emit(node);
    }
  };

  shouldHide(node: Directory): boolean {
    let parent = node.parentRef;
    while (parent) {
      if (parent.isExpanded === false) {
        return true;
      }
      parent = parent.parentRef;
    }

    return false;
  }

  trajectoryName: string;
  trajectoryDescription: string;
  trajectoryVersion: string;
  trajectoryPath: string;
  trajectorySearchKeys: string;
  infoAccordionValue: string = null;
  infoExpanded: boolean = false;

  toggleInfoPanel(event: Event): void {
    this.infoExpanded = !this.infoExpanded;
  }

  defaultAltitude: number = 100;
  defaultSpeed: number = 5;

  directoryTreeParent: Directory[] = [];
  trajectoryList: AutopilotTrajectory[] = [];

  forkCounter: number = 0;
  initialCounter: number = 0;
  finalCounter: number = 0;

  isLoaded: boolean = false;

  isDevel() {
    return this.profile === 'devel';
  }

  get getButtonText() {
    return this.isLoaded ? 'FP.INFO.BUTTON_UPDATE' : 'FP.INFO.BUTTON_CREATE';
  }

  @Output() addInitial: EventEmitter<any> = new EventEmitter();
  @Output() addArrival: EventEmitter<any> = new EventEmitter();
  @Output() addFork: EventEmitter<any> = new EventEmitter();

  @Output() selectTrack: EventEmitter<Directory> = new EventEmitter();

  @Output() createdInitial: EventEmitter<string> = new EventEmitter();
  @Output() createdFinal: EventEmitter<string> = new EventEmitter();

  @Output() changeWaypointParameters: EventEmitter<AutopilotCommand> =
    new EventEmitter();
  @Output() removeWaypoint: EventEmitter<AutopilotCommand> = new EventEmitter();
  @Output() showWaypoint: EventEmitter<AutopilotCommand> = new EventEmitter();

  @Output() createAPT: EventEmitter<any> = new EventEmitter();
  @Output() updateAPT: EventEmitter<any> = new EventEmitter();
  @Output() saveAPT: EventEmitter<any> = new EventEmitter();

  @Input() currentBranch: string = null;
  @Input() currentStage: string = 'Departure';
  @Output() stageChanged: EventEmitter<string> = new EventEmitter();

  selectStage(stage: string): void {
    this.stageChanged.emit(stage);
  }

  get departureBranches(): any[] {
    if (!this.tree) return [];
    return this.tree.obtainArray().filter((n: any) =>
      n.level === 1 && n.parentRef?.name === 'Departure'
    );
  }

  get missionDirectory(): any {
    return this.tree?.searchByName('Mission')?.data || null;
  }

  get arrivalBranches(): any[] {
    if (!this.tree) return [];
    return this.tree.obtainArray().filter((n: any) =>
      n.level === 1 && n.parentRef?.name === 'Arrival'
    );
  }

  get departureWaypointCount(): number {
    return this.departureBranches.reduce(
      (sum: number, branch: any) => sum + this.countWaypointsInItems(branch.items || []), 0
    );
  }

  get missionWaypointCount(): number {
    return this.countWaypointsInItems(this.missionDirectory?.items || []);
  }

  get arrivalWaypointCount(): number {
    return this.arrivalBranches.reduce(
      (sum: number, branch: any) => sum + this.countWaypointsInItems(branch.items || []), 0
    );
  }

  countWaypointsInItems(items: any[]): number {
    return items.reduce((sum: number, item: any) => {
      if (!item.name) return sum + 1;
      return sum + this.countWaypointsInItems(item.items || []);
    }, 0);
  }

  /**
   * METHODS TO PASS INFO TO THE PARENT COMPONENT ----------------------------------------------------------------------------------------------------------------------
   */

  saveTrajectory(e) {
    e.stopPropagation(e);
    this.saveAPT.emit();
  }

  createOrUpdate(e) {
    e.stopPropagation(e);
    const isValid = this.trajectoryName && this.trajectoryVersion &&
                    this.trajectoryPath && this.trajectoryDescription &&
                    this.trajectorySearchKeys;
    if (!isValid) {
      this.infoAccordionValue = 'first';
      return;
    }
    this.infoAccordionValue = null;
    if (this.isLoaded) {
      this.updateAPT.emit();
    } else {
      this.createAPT.emit();
    }
  }

  addInitialArrivalTrack(node: any) {
    if (node.name === 'Departure') {
      this.addInitial.emit();
    } else if (node.name === 'Arrival') {
      this.addArrival.emit();
    }
  }

  addForkTrack(node: any) {
    this.addFork.emit(node);
  }

  editWaypoint(autopilotCommand: AutopilotCommand) {
    this.changeWaypointParameters.emit(autopilotCommand);
  }

  eraseWaypoint(waypoint: AutopilotCommand) {
    this.removeWaypoint.emit(waypoint);
  }

  showWaypointOnMap(waypoint: AutopilotCommand) {
    this.showWaypoint.emit(waypoint);
  }

  //-----------------------------------------------------------------------------------------------------------------------------------------------------------------/

  suppressAutoScroll = false;
  private prevWaypointCount = 0;

  constructor(
    private ref: ChangeDetectorRef,
    private el: ElementRef,
    private modalController: ModalController,
    private autopilotService: AutopilotService,
    private directoryService: DirectoryService,
  ) {}

  ngOnInit(): void {
    if (this.user?.profile === 'devel') {
      this.profile = 'devel';
    }

    this.initializeTrajectoryTree();
  }

  loadFlightPlanData(trajectory: AutopilotTrajectory) {
    this.trajectoryName = trajectory.name;
    this.trajectoryDescription = trajectory.description;
    this.trajectoryVersion = trajectory.version;
    this.trajectoryPath = trajectory.path;
    this.trajectorySearchKeys = trajectory.searchKeys;
  }

  /**
   * Update tree data after any change and force to be visible in the interface
   */
  updateTree() {
    this.dataSource = new ArrayDataSource(this.tree.obtainArray());
    this.ref.detectChanges();
    const currentCount = this.departureWaypointCount + this.missionWaypointCount + this.arrivalWaypointCount;
    if (!this.suppressAutoScroll && currentCount > this.prevWaypointCount) {
      setTimeout(() => this.scrollToBottom(), 50);
    }
    this.prevWaypointCount = currentCount;
  }

  scrollToBottom(): void {
    const host = this.el.nativeElement as HTMLElement;
    host.scrollTop = host.scrollHeight;
  }

  scrollToWaypoint(waypoint: AutopilotCommand): void {
    const host = this.el.nativeElement as HTMLElement;
    const el = host.querySelector<HTMLElement>(`[data-wp-idx="${waypoint.index}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * Method to apply styles to the tree nodes and show in which stage the waypoint is
   * @param node: Specific node in the tree
   * @returns the styles to apply to the node
   */
  applyStyles(node: Directory) {
    let styles;
    if (
      node.name === 'Departure' ||
      node.name.includes('Initial') ||
      node.parentRef?.name === 'Departure' ||
      node.parentRef?.name.includes('Initial')
    ) {
      styles = { border: 'solid rgb(255, 89, 94, 0.7)', backgroundColor: 'rgba(255, 89, 94, 0.08)' };
      return styles;
    }
    if (node.name === 'Mission' || node.parentRef?.name === 'Mission') {
      styles = { border: 'solid rgb(138, 201, 38, 0.7)', backgroundColor: 'rgba(138, 201, 38, 0.08)' };
      return styles;
    }
    if (
      node.name === 'Arrival' ||
      node.name.includes('Arrival') ||
      node.parentRef?.name === 'Arrival' ||
      node.parentRef?.name.includes('Arrival')
    ) {
      styles = { border: 'solid rgb(50, 108, 235, 0.7)', backgroundColor: 'rgba(50, 108, 235, 0.08)' };
      return styles;
    }
  }

  async handlePathChange(event: Event) {
    event.preventDefault();
    let directoryTree = [
      ...this.directoryService.buildTree(this.trajectoryList as PlanningItem[]),
    ];

    const modal = await this.modalController.create({
      component: DirectoryManagerComponent,
      componentProps: {
        objectType: 'trajectory',
        directoryTree: directoryTree,
        parentDirectoryTree: this.directoryTreeParent,
        selectedPath: this.trajectoryPath,
      },
    });
    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    this.directoryTreeParent = [...data.directoryTreeChild];

    if (role === 'confirm') {
      this.trajectoryPath = data.path;
    }
  }

  /**
   * Initialize the tree with the main stages of the trajectory (used at the beginning or when removing the whole trajectory)
   */
  initializeTrajectoryTree() {
    this.trajectoryName = '';
    this.trajectoryDescription = '';
    this.trajectoryVersion = '';
    this.trajectoryPath = '';
    this.trajectorySearchKeys = '';

    this.autopilotService.getAllAutopilotTrajectories();
    this.autopilotService.apTrajectories$.subscribe((trajectories: AutopilotTrajectory[]) => {
      trajectories.forEach((trajectory: AutopilotTrajectory)=>{
        this.trajectoryList.push(trajectory);
      });
    });

    this.forkCounter = 0;
    this.initialCounter = 0;
    this.finalCounter = 0;
    this.tree = new LinkedList<Directory>((a, b) => a === b);
    let departure: Directory = {
      selected: false,
      pseudonym: 'Departure',
      name: 'Departure',
      level: 0,
      isExpanded: true,
      badgeValue: 0,
      parentRef: null,
      items: [],
    };

    let mission: Directory = {
      selected: false,
      pseudonym: 'Mission',
      name: 'Mission',
      level: 0,
      isExpanded: true,
      badgeValue: 0,
      parentRef: null,
      items: [],
    };

    let arrival: Directory = {
      selected: false,
      pseudonym: 'Arrival',
      name: 'Arrival',
      level: 0,
      isExpanded: true,
      badgeValue: 0,
      parentRef: null,
      items: [],
    };
    this.tree.append(departure);
    this.tree.append(mission);
    this.tree.append(arrival);

    this.updateTree();
  }

  /**
   * Method to remove an entry from the tree wheter it has been removed directly from the tre or after removing a selection of waypoints
   * @param waypoint
   */
  removeWaypointFromTree(waypoint: AutopilotCommand) {
    this.tree.obtainArray().forEach((item) => {
      item.items.forEach((subItem, indexSubitem) => {
        if (!subItem.items && subItem === waypoint) {
          if (waypoint.apCommand === AutopilotCommandEnum.ForkRoute) {
            item.items.find((it, i) => {
              if (it.name === 'Fork ' + (this.forkCounter - 1)) {
                item.items.splice(i, 1);
                this.forkCounter--;
              }
            });
          }

          item.items.splice(indexSubitem, 1);
        } else if (subItem.items) {
          subItem.items.forEach((subSubItem, subIndex) => {
            if (subSubItem === waypoint) {
              subItem.items.splice(subIndex, 1);
            }
          });
        }
      });
    });

    this.updateTree();
  }

  /**
   * Method to obtain the specific number of waypoints remaining in a fork
   * @param stageLabel: Label of the stage where the fork is located
   * @param forkLabel: Label of the fork
   * @returns the number of waypoints in the fork
   */
  obtainWaypointsAtFork(stageLabel: string, forkLabel: string): number {
    let waypointsInFork;
    this.tree.searchByName(stageLabel).data.items.forEach((item) => {
      if (item.name === forkLabel) {
        waypointsInFork = item.items.length;
      }
    });
    return waypointsInFork;
  }

  /**
   * Method to add a waypoint to the tree
   * @param stage: Stage where the waypoint is located
   * @param state: state of the trajectory (TrajectoryState)
   * @param apCommand: AutopilotCommand to add (waypoint)
   * @param currentBranch: Branch where the waypoint is located (only if stage is Departure or Arrival)
   * @returns: true if the waypoint has been added successfully, false otherwise
   */
  addWaypoint(
    stage: string,
    state: TrajectoryState,
    apCommand: AutopilotCommand,
    currentBranch: string,
  ): boolean {
    try {
      let trajectoryStage: Directory;

      if (
        state === TrajectoryState.InForkTrack ||
        state === TrajectoryState.CreatingForkTrack ||
        state === TrajectoryState.CreatingMissionAreaInFork ||
        state === TrajectoryState.ClosingMissionAreaInFork
      ) {
        trajectoryStage = this.obtainCurrentFork(
          stage,
          currentBranch,
          apCommand.forkLabel,
        );
        trajectoryStage.items.push(apCommand as PlanningItem);
      } else {
        switch (stage) {
          case 'Departure':
            trajectoryStage = this.tree.searchByName(currentBranch).data;
            trajectoryStage.items.push(apCommand as PlanningItem);
            break;
          case 'Mission':
            this.tree
              .searchByName(stage)
              .data.items.push(apCommand as PlanningItem);
            break;
          case 'Arrival':
            trajectoryStage = this.tree.searchByName(currentBranch).data;
            trajectoryStage.items.push(apCommand as PlanningItem);
            break;
        }
      }

      this.updateTree();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /**
   * Method to add a waypoint to the tree at a specific index (used to add a midle waypoint between two existing waypoints)
   * @param stage: Stage where the waypoint is located.
   * @param apCommand: AutopilotCommand to add (waypoint).
   * @param followingApCommand: AutopilotCommand that follows the new waypoint.
   * @param previousApCommand: AutopilotCommand that precedes the new waypoint.
   * @returns: true if the waypoint has been added successfully, false otherwise.
   */
  addWaypointAtIndex(
    stage: string,
    apCommand: AutopilotCommand,
    followingApCommand: AutopilotCommand,
    previousApCommand: AutopilotCommand,
  ): boolean {
    try {
      let trajectoryStage: Directory;

      if (
        apCommand.stateTrajectory === TrajectoryState.InForkTrack ||
        apCommand.stateTrajectory === TrajectoryState.CreatingForkTrack
      ) {
        trajectoryStage = this.obtainSpecificFork(
          apCommand.stageLabel,
          apCommand.branchLabel,
          apCommand.forkLabel,
        );
        if (followingApCommand.apCommand === 'JoinRoute') {
          trajectoryStage.items.splice(
            trajectoryStage.items.findIndex((it) => it === previousApCommand) +
              1,
            0,
            apCommand as PlanningItem,
          );
        } else {
          trajectoryStage.items.splice(
            trajectoryStage.items.findIndex((it) => it === followingApCommand),
            0,
            apCommand as PlanningItem,
          );
        }
      } else {
        switch (stage) {
          case 'Departure':
            trajectoryStage = this.tree.searchByName(
              apCommand.branchLabel,
            ).data;
            trajectoryStage.items.splice(
              trajectoryStage.items.findIndex(
                (it) => it === followingApCommand,
              ),
              0,
              apCommand as PlanningItem,
            );
            break;
          case 'Mission':
            this.tree.searchByName(stage).data.items.splice(
              this.tree
                .searchByName(stage)
                .data.items.findIndex((it) => it === followingApCommand),
              0,
              apCommand as PlanningItem,
            );
            break;
          case 'Arrival':
            trajectoryStage = this.tree.searchByName(
              apCommand.branchLabel,
            ).data;
            trajectoryStage.items.splice(
              trajectoryStage.items.findIndex(
                (it) => it === followingApCommand,
              ),
              0,
              apCommand as PlanningItem,
            );
            break;
        }
      }

      this.updateTree();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /**
   * Method to obtain the current fork that we are in (Directory).
   * @param stage: Stage where the fork is located.
   * @param currentBranch: Branch where the fork is located.
   * @returns: node that represents the fork so we can access all the information.
   */
  obtainCurrentFork(
    stage: string,
    currentBranch: string,
    forkLabel: string,
  ): Directory {
    let trajectoryStage: Directory;

    switch (stage) {
      case 'Departure': {
        this.tree.searchByName(currentBranch).data.items.forEach((item) => {
          if (item.name && item.name == forkLabel) {
            trajectoryStage = this.tree
              .searchByName(currentBranch)
              .data.items.find((it) => it.name === item.name);
          }
        });
        break;
      }
      case 'Mission': {
        this.tree.searchByName(stage).data.items.forEach((item) => {
          if (item.name && item.name == forkLabel) {
            trajectoryStage = this.tree
              .searchByName(stage)
              .data.items.find((it) => it.name === item.name);
          }
        });
        break;
      }
      case 'Arrival': {
        this.tree.searchByName(currentBranch).data.items.forEach((item) => {
          if (item.name && item.name == forkLabel) {
            trajectoryStage = this.tree
              .searchByName(currentBranch)
              .data.items.find((it) => it.name === item.name);
          }
        });
        break;
      }
    }

    return trajectoryStage;
  }

  /**
   * Method to obtain a specific fork (Directory)
   * @param stage: Stage where the fork is located.
   * @param branch: Branch where the fork is located.
   * @param fork: label of the fork to obtain.
   * @returns: node that represents the fork so we can access all the information.
   */
  obtainSpecificFork(stage: string, branch: string, fork: string): Directory {
    let trajectoryStage: Directory;

    switch (stage) {
      case 'Departure': {
        this.tree.searchByName(branch).data.items.forEach((item) => {
          if (item.name && item.name === fork) {
            trajectoryStage = this.tree
              .searchByName(branch)
              .data.items.find((it) => it.name === item.name);
          }
        });
        if (!trajectoryStage) {
          let found = false;
          this.tree.searchByName(branch).data.items.forEach((item) => {
            if (item.name && item.name.includes('Fork') && !found) {
              trajectoryStage = this.tree
                .searchByName(branch)
                .data.items.find((it) => it.name === item.name);
              found = true;
            }
          });
        }
        break;
      }
      case 'Mission': {
        this.tree.searchByName(stage).data.items.forEach((item) => {
          if (item.name && item.name === fork) {
            trajectoryStage = this.tree
              .searchByName(stage)
              .data.items.find((it) => it.name === item.name);
          }
        });
        if (!trajectoryStage) {
          let found = false;
          this.tree.searchByName(stage).data.items.forEach((item) => {
            if (item.name && item.name.includes('Fork') && !found) {
              trajectoryStage = this.tree
                .searchByName(stage)
                .data.items.find((it) => it.name === item.name);
              found = true;
            }
          });
        }
        break;
      }
      case 'Arrival': {
        this.tree.searchByName(branch).data.items.forEach((item) => {
          if (item.name && item.name === fork) {
            trajectoryStage = this.tree
              .searchByName(branch)
              .data.items.find((it) => it.name === item.name);
          }
        });
        if (!trajectoryStage) {
          let found = false;
          this.tree.searchByName(branch).data.items.forEach((item) => {
            if (item.name && item.name.includes('Fork') && !found) {
              trajectoryStage = this.tree
                .searchByName(branch)
                .data.items.find((it) => it.name === item.name);
              found = true;
            }
          });
        }
        break;
      }
    }

    return trajectoryStage;
  }

  /**
   * Method to update the coordinates of a waypoint after dragging it on the map
   * @param newCoordinates: New coordinates of the waypoint.
   * @param apCommand: AutopilotCommand to update (waypoint).
   */
  updateCoordinatesAfterDrag(
    newCoordinates: L.LatLng,
    apCommand: AutopilotCommand,
  ): void {
    newCoordinates.lat = parseFloat(newCoordinates.lat.toFixed(7));
    newCoordinates.lng = parseFloat(newCoordinates.lng.toFixed(7));
    this.tree.obtainArray().forEach((item) => {
      item.items.forEach((subItem) => {
        if (subItem === apCommand) {
          subItem.point.latitude = newCoordinates.lat;
          subItem.point.longitude = newCoordinates.lng;
        } else if (subItem.items) {
          subItem.items.forEach((subSubItem) => {
            if (subSubItem === apCommand) {
              subSubItem.point.latitude = newCoordinates.lat;
              subSubItem.point.longitude = newCoordinates.lng;
            }
          });
        }
      });
    });
    this.updateTree();
  }

  /**
   * Method to create a new entry in the tree for a new initial track.
   * @returns: true if the initial track has been created successfully, false otherwise.
   */
  startInitialEntry(): boolean {
    try {
      while (this.tree.searchByName('Track Initial ' + this.initialCounter)) {
        this.initialCounter++;
      }
      let stageDirectory = this.tree.searchByName('Departure').data;
      let newEntry: Directory = {
        selected: false,
        pseudonym: 'Track Initial ' + this.initialCounter,
        name: 'Track Initial ' + this.initialCounter,
        level: 1,
        isExpanded: true,
        badgeValue: 0,
        parentRef: stageDirectory,
        items: [],
      };

      this.createdInitial.emit(newEntry.name);

      this.initialCounter++;
      this.tree.insertAt(
        newEntry,
        this.tree.searchByNameIndex('Departure') + 1,
      );
      this.updateTree();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /**
   * Method to create a new entry in the tree for a new arrival track.
   * @returns: true if the arrival track has been created successfully, false otherwise.
   */
  startArrivalEntry(): boolean {
    try {
      while (this.tree.searchByName('Track Arrival ' + this.finalCounter)) {
        this.finalCounter++;
      }
      let stageDirectory = this.tree.searchByName('Arrival').data;
      let newEntry: Directory = {
        selected: false,
        pseudonym: 'Track Arrival ' + this.finalCounter,
        name: 'Track Arrival ' + this.finalCounter,
        level: 1,
        isExpanded: true,
        badgeValue: 0,
        parentRef: stageDirectory,
        items: [],
      };

      this.createdFinal.emit(newEntry.name);

      this.finalCounter++;
      this.tree.insertAt(newEntry, this.tree.searchByNameIndex('Arrival') + 1);
      this.updateTree();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /**
   * Method to create a new entry in the tree for a new fork.
   * @param stage: Stage where the fork is located.
   * @param state: State of the trajectory (TrajectoryState).
   * @param waypoint: forkRoute waypoint where the fork will start.
   * @param currentBranch: Branch where the fork is located.
   * @returns: JoinRoute waypoint if there is already a fork created, null otherwise.
   */
  startForkEntry(
    stage: string,
    state: TrajectoryState,
    waypoint: AutopilotCommand,
    currentBranch: string,
  ): AutopilotCommand {
    try {
      let stageDirectory: Directory;
      let newEntry: Directory;

      switch (state) {
        case TrajectoryState.InInitialTrack: {
          stageDirectory = this.tree.searchByName(currentBranch).data;
          newEntry = {
            selected: false,
            pseudonym: 'Fork ' + this.forkCounter,
            name: 'Fork ' + this.forkCounter,
            level: 2,
            isExpanded: true,
            badgeValue: 0,
            parentRef: stageDirectory,
            items: [],
          };

          this.forkCounter++;
          break;
        }

        case TrajectoryState.CreatingArrivalTrack:
        case TrajectoryState.InArrivalTrack: {
          stageDirectory = this.tree.searchByName(currentBranch).data;
          newEntry = {
            selected: false,
            pseudonym: 'Fork ' + this.forkCounter,
            name: 'Fork ' + this.forkCounter,
            level: 2,
            isExpanded: true,
            badgeValue: 0,
            parentRef: stageDirectory,
            items: [],
          };

          this.forkCounter++;
          break;
        }

        case TrajectoryState.InMission: {
          stageDirectory = this.tree.searchByName(stage).data;
          newEntry = {
            selected: false,
            pseudonym: 'Fork ' + this.forkCounter,
            name: 'Fork ' + this.forkCounter,
            level: 1,
            isExpanded: true,
            badgeValue: 0,
            parentRef: stageDirectory,
            items: [],
          };

          this.forkCounter++;
          break;
        }
      }

      if (!waypoint) {
        stageDirectory.items.push(newEntry as PlanningItem);

        this.updateTree();
        return null;
      } else {
        let joinRouteWaypoint;
        let nextJoin = false;
        stageDirectory.items.forEach((item) => {
          if (
            item?.point?.latitude == waypoint?.point?.latitude &&
            item?.point?.longitude == waypoint?.point?.longitude
          ) {
            nextJoin = true;
          }
          if (nextJoin && item.label === 'JoinRoute') {
            stageDirectory.items.splice(
              stageDirectory.items.findIndex((it) => it === item),
              0,
              newEntry as PlanningItem,
            );
            nextJoin = false;
            this.updateTree();
            joinRouteWaypoint = item;
            return;
          }
        });

        return joinRouteWaypoint;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}
